<template>
  <form-page-template
      :dirty="dirty"
      @save="doSave"
      @pane-resizing="measureTabItemHeight"
      fluid
      maximizable>
    <template #toolbar-items>
      <v-toolbar-title class="with-divider">
        サービス実装
      </v-toolbar-title>
    </template>

    <service-implementation-info
        :serviceImplementation="serviceImplementation"
        :editable="isEditable"
        @dirty="markDirty()">
    </service-implementation-info>

    <template #left-tabs>
      <el-table
          :data="processImplementationsView"
          row-key="id"
          highlight-current-row
          :style="{width: '30%', flex: 'none'}"
          @current-change="handleProcessImplementationSelectionChange"
          :default-sort="{prop: 'name', order: 'ascending'}">

        <el-table-column label="プロセス実装">
          <el-table-column
              label="名称"
              prop="name"
              width="200"
              sortable>
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.name"
                    :value-local-id="row.localId">
                  <template #default="{value}">
                    {{ value }}
                    <error-popover :errors="row.errors"></error-popover>
                  </template>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>

          <el-table-column
              label="説明"
              prop="description">
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.description"
                    placeholder="未設定"
                    multi-line>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>
        </el-table-column>

        <el-table-column
            width="55"
            align="right">
          <template #default="{row}">
            <icon-btns>
<!--              <icon-btn-->
<!--                  type="delete"-->
<!--                  @click="openDeleteDialog(row)">-->
<!--              </icon-btn>-->
              <icon-btn
                  type="edit-content"
                  @click="openUpdateDialog(row)">
              </icon-btn>
            </icon-btns>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <template #tab-items>
      <v-tab>
        イベント実装
        <el-tooltip
            v-if="selectedProcessImplementation && selectedProcessImplementation.eventImplementationsCount === 0"
            content="イベント実装が登録されていません"
            placement="bottom">
          <error-icon level="warn" class="ml-1" small dark></error-icon>
        </el-tooltip>
      </v-tab>
      <v-tab-item>
        <event-implementation-table
            :height="tabItemHeight"
            :process-implementation="selectedProcessImplementation"
            @update-count="handleUpdateEventImplementationsCount">
        </event-implementation-table>
      </v-tab-item>

      <v-tab>
        トリガとなるイベント実装
        <el-tooltip
            v-if="selectedProcessImplementation && selectedProcessImplementation.triggerImplementationsCount === 0"
            content="トリガとなるイベント実装が登録されていません"
            placement="bottom">
          <error-icon level="info" class="ml-1" small dark></error-icon>
        </el-tooltip>
      </v-tab>
      <v-tab-item>
        <trigger-implementation-table
            :height="tabItemHeight"
            :process-implementation="selectedProcessImplementation"
            :service-implementation="serviceImplementationView"
            @update-count="handleUpdateTriggerImplementation">
        </trigger-implementation-table>
      </v-tab-item>

      <v-tab>プロセス手順実装</v-tab>
      <v-tab-item>
        <process-operation-implementation-table
            :height="tabItemHeight"
            :process-implementation="selectedProcessImplementation"
            :service-implementation="serviceImplementationView">
        </process-operation-implementation-table>
      </v-tab-item>
    </template>

    <!-- プロセス実装編集ダイアログ -->
    <simple-dialog
        v-model="dialogUpdate.visible"
        title-text="プロセス実装編集"
        @close="closeUpdateDialog">
      <process-implementation-form
          v-model="dialogUpdate.form">
      </process-implementation-form>
    </simple-dialog>

    <!-- プロセス実装編集ダイアログ -->
    <simple-dialog
        v-model="dialogDelete.visible"
        title-text="プロセス実装削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のプロセス実装を削除します。本当によろしいですか？"
        @close="closeDeleteDialog">
      <process-implementation-form
          v-model="dialogDelete.form"
          readonly>
      </process-implementation-form>
    </simple-dialog>
  </form-page-template>
</template>

<script>
import {mapState, mapActions, mapGetters, mapMutations} from "vuex";
import gql from "graphql-tag";
import BetterSet from "betterset";
import PathInfoMixin from "@/router/PathInfoMixin";
import IoablePageMixin from "@/router/IoablePageMixin";
import ServiceImplementationInfo from "@/components/organisms/ServiceImplementationInfo.vue";
import EventImplementationTable from "@/components/pages/implementation/ServiceImplementationShow/EventImplementationTable.vue";
import TriggerImplementationTable from "@/components/pages/implementation/ServiceImplementationShow/TriggerImplementationTable.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ErrorPopover from "@/components/molecules/ErrorPopover.vue";
import ErrorIcon from "@/components/molecules/ErrorIcon.vue";
import FormPageTemplate from "@/components/templates/FormPageTemplate.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import ProcessImplementationForm
  from "@/components/pages/implementation/ServiceImplementationShow/ProcessImplementationForm";
import ProcessOperationImplementationTable
  from "@/components/pages/implementation/ServiceImplementationShow/ProcessOperationImplementationTable";
import Formatter from "@/utils/Formatter";

export default {
  name: 'ServiceImplementationShow',
  components: {
    ProcessOperationImplementationTable,
    ProcessImplementationForm,
    SimpleDialog,
    IconBtn,
    IconBtns,
    FormPageTemplate,
    ErrorIcon,
    ErrorPopover,
    TextFieldLike,
    TableInline,
    ServiceImplementationInfo,
    EventImplementationTable,
    TriggerImplementationTable,
  },
  mixins: [
    PathInfoMixin,
    IoablePageMixin,
    Formatter,
  ],
  props: {
    serviceImplementationId: {
      type: String,
      required: true
    }
  },
  provide() {
    return {
      getProcessImplementations: () => this.processImplementations
    }
  },
  data() {
    return {
      selectedProcessImplementation: null,
      serviceImplementation: {},
      processImplementations: [],
      tabItemHeight: null,
      dialogUpdate: {
        visible: false,
        form: null
      },
      dialogDelete: {
        visible: false,
        form: null
      },
    };
  },
  mounted() {
    this.measureTabItemHeight();
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
      hdsEntities: 'hds/entities'
    }),
    serviceImplementationView() {
      return {
        ...this.serviceImplementation,
        serviceComponentModel:
            this.hdsEntities.serviceComponentModel.findOne({id: this.serviceImplementation.serviceComponentModelId})
      };
    },
    processImplementationsView() {
      return this.processImplementations
        .filter(pi => {
          return pi.serviceImplementationId === this.serviceImplementationId
        })
        .map(pi => {
          const pcm = this.hdsEntities.processComponentModel.findOne({id: pi.processComponentModelId});
          const errors = [];
          if(pi.eventImplementationsCount === 0) {
            errors.push({
              level: 'warn',
              message: 'イベント実装が登録されていません'
            });
          }
          if(pi.triggerImplementationsCount === 0) {
            errors.push({
              level: 'info',
              message: 'トリガとなるイベント実装が登録されていません'
            });
          }
          return {
            ...pi,
            name: pcm?.name,
            description: pcm?.description,
            processComponentModel: pcm,
            errors
          };
        });
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlQueryAndLoad: 'hds/gqlQueryAndLoad',
      gqlQuery: 'hds/gqlQuery'
    }),
    handleProcessImplementationSelectionChange(row) {
      this.selectedProcessImplementation = row;
    },
    async load() {
      const {data: serviceImplementation} = await this.hcsClient.get(`/service_implementations/${this.serviceImplementationId}`);
      this.serviceImplementation = serviceImplementation;

      const {data: processImplementations} = await this.hcsClient.get('/process_implementations', {
        params: {
          'q[service_implementation_id_eq]': this.serviceImplementationId
        }
      });
      this.processImplementations = processImplementations;

      const processComponentModelIds = this.processImplementations
          .map(pi => pi.processComponentModelId)
          .reduce((set, value) => {set.add(value); return set;}, new BetterSet())
          .asArray()
          .filter(v => !!v);

      await this.gqlQueryAndLoad({
        query: gql`
            query FindProcessComponentModels(
                $processComponentModelIds: [ID!]!,
                $serviceComponentModelId: ID!
            ) {
                processComponentModels: processComponentModelInBulk(processComponentModelIds: $processComponentModelIds) {
                    id
                    name
                    description
                }
                serviceComponentModel(serviceComponentModelId: $serviceComponentModelId) {
                    id
                    name
                    description
                }
            }`,
        variables: {
          processComponentModelIds,
          serviceComponentModelId: this.serviceImplementation.serviceComponentModelId
        },
        mapping: {
          processComponentModel: 'processComponentModels*',
          serviceComponentModel: 'serviceComponentModel*'
        }
      });
    },
    save() {
      return this.hcsClient.patch(`/service_implementations/${this.serviceImplementationId}`, {
        localId: this.serviceImplementation.localId,
        remarks: this.serviceImplementation.remarks,
        publishing: this.serviceImplementation.publishing
      });
    },
    handleUpdateEventImplementationsCount(count) {
      this.selectedProcessImplementation.eventImplementationsCount = count;
      const pi = this.processImplementations.find(pi => pi.id === this.selectedProcessImplementation.id);
      if(pi) {
        pi.eventImplementationsCount = count;
      }
    },
    handleUpdateTriggerImplementation(count) {
      this.selectedProcessImplementation.triggerImplementationsCount = count;
      const pi = this.processImplementations.find(pi => pi.id === this.selectedProcessImplementation.id);
      if(pi) {
        pi.triggerImplementationsCount = count;
      }
    },
    measureTabItemHeight() {
      const tabsEl = this.$el.querySelector('.v-tabs');
      if(!tabsEl) {
        return;
      }
      const tabsBarEl = tabsEl.querySelector(':scope > .v-tabs-bar');
      if(!tabsBarEl) {
        return;
      }
      this.tabItemHeight = tabsEl.clientHeight - tabsBarEl.clientHeight;
    },
    openUpdateDialog(processImplementation) {
      this.dialogUpdate = {
        visible: true,
        form: processImplementation
      };
    },
    async closeUpdateDialog(apply) {
      if(apply) {
        this.showSnackbar({
          message: 'プロセス実装を更新中...',
          timeout: false,
          loading: true
        });
        try {
          this.dialogUpdate.visible = false;
          const form = this.dialogUpdate.form;
          const processImplementation = await this.$store.dispatch('hcs/processImplementation/update', form);
          const index = this.processImplementations.findIndex(pi => pi.id === form.id);
          if(index !== -1) {
            this.processImplementations.splice(index, 1, processImplementation);
          }
          this.showSnackbar({
            message: `プロセス実装が更新されました`
          });
        } catch (e) {
          this.showSnackbar({
            color: 'failed',
            message: this.formatError(e, 'プロセス実装の更新に失敗しました')
          });
        }
      } else {
        this.dialogUpdate.visible = false;
      }
    },
    openDeleteDialog(processImplementation) {
      this.dialogDelete = {
        visible: true,
        form: processImplementation
      };
    },
    async closeDeleteDialog(apply) {
      if(apply) {
        this.showSnackbar({
          message: 'プロセス実装を削除中...',
          timeout: false,
          loading: true
        });
        try {
          this.dialogDelete.visible = false;
          await this.$store.dispatch('hcs/processImplementation/delete', this.dialogDelete.form.id);
          this.showSnackbar({
            message: `プロセス実装が削除されました`
          });
        } catch (e) {
          this.showSnackbar({
            color: 'failed',
            message: `プロセス実装の削除に失敗しました`
          });
        }
      } else {
        this.dialogDelete.visible = false;
      }
    },
  }
}
</script>
